import type { WorkflowDiagram, WorkflowDiagramEdge } from '@/api/workflowDiagram';
import type { WorkflowCatalogContext } from '@/api/types';

// Pure layout helpers for the workflow map. No React, no DOM — the map component reads these.

/** The node ids in run order when the workflow is one straight line (one start, each step has at most one next); else null. */
export function linearOrder(diagram: WorkflowDiagram): string[] | null {
  const ids = diagram.nodes.map((n) => n.id);
  if (ids.length === 0) return [];
  const out = new Map<string, string[]>();
  const inCount = new Map<string, number>(ids.map((id) => [id, 0]));
  for (const e of diagram.edges) {
    out.set(e.source, [...(out.get(e.source) ?? []), e.target]);
    inCount.set(e.target, (inCount.get(e.target) ?? 0) + 1);
  }
  if ([...out.values()].some((t) => t.length > 1)) return null;
  if ([...inCount.values()].some((c) => c > 1)) return null;
  const roots = ids.filter((id) => (inCount.get(id) ?? 0) === 0);
  if (roots.length !== 1) return null;

  const order: string[] = [];
  const seen = new Set<string>();
  let current: string | undefined = roots[0];
  while (current && !seen.has(current)) {
    order.push(current);
    seen.add(current);
    current = out.get(current)?.[0];
  }
  return order.length === ids.length ? order : null;
}

/**
 * Each node's rank (row) for a branching workflow: the LONGEST path from a start, so a step that merges two
 * branches sits below both. Cycles are cut by never revisiting a node more times than there are nodes.
 */
export function ranks(diagram: WorkflowDiagram): Map<string, number> {
  const ids = diagram.nodes.map((n) => n.id);
  const incoming = new Set(diagram.edges.map((e) => e.target));
  const starts = diagram.nodes.filter((n) => n.isStart).map((n) => n.id);
  const roots = starts.length > 0 ? starts : ids.filter((id) => !incoming.has(id));
  const rank = new Map<string, number>(roots.map((id) => [id, 0]));

  for (let pass = 0; pass < ids.length; pass++) {
    let changed = false;
    for (const e of diagram.edges) {
      const from = rank.get(e.source);
      if (from == null) continue;
      if ((rank.get(e.target) ?? -1) < from + 1) {
        rank.set(e.target, from + 1);
        changed = true;
      }
    }
    if (!changed) break;
  }
  for (const id of ids) if (!rank.has(id)) rank.set(id, 0);
  return rank;
}

/** Run order for numbering steps: by rank, then by declaration. */
export function runOrder(diagram: WorkflowDiagram): string[] {
  const linear = linearOrder(diagram);
  if (linear) return linear;
  const r = ranks(diagram);
  return diagram.nodes
    .map((n, i) => ({ id: n.id, rank: r.get(n.id) ?? 0, i }))
    .sort((a, b) => a.rank - b.rank || a.i - b.i)
    .map((x) => x.id);
}

/** Which contexts each step writes and reads. */
export function contextRoles(contexts: WorkflowCatalogContext[] | undefined): Map<string, { writes: WorkflowCatalogContext[]; reads: WorkflowCatalogContext[] }> {
  const roles = new Map<string, { writes: WorkflowCatalogContext[]; reads: WorkflowCatalogContext[] }>();
  const of = (id: string) => {
    if (!roles.has(id)) roles.set(id, { writes: [], reads: [] });
    return roles.get(id)!;
  };
  for (const ctx of contexts ?? []) {
    for (const id of ctx.writtenBy) of(id).writes.push(ctx);
    for (const id of ctx.readBy) of(id).reads.push(ctx);
  }
  return roles;
}

/** The edge from one step to another, if any. */
export function edgeBetween(edges: WorkflowDiagramEdge[], source: string, target: string): WorkflowDiagramEdge | undefined {
  return edges.find((e) => e.source === source && e.target === target);
}

/** "1 skill" / "3 skills". */
export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}
